import type { ReactNode } from 'react';
import type { PanelOverlay } from './use-panel-overlay';
import { useEffect } from 'react';
import { cn } from 'helpers/cn';
import { trackUmami } from '@/lib/umami/umami';
import { useSigninAnnouncements } from '@/state/useSigninPanel';
import { SignupChat } from '../signup/SignupChat';
import { useSignupChat } from '../signup/use-signup-chat';
import { NewsPanel } from './NewsPanel';
import { useNewsChat } from './use-news-chat';

/**
 * What fills the full screen panel instead of the signup chat. Handed `close`, which
 * slides the panel back to its slot and reveals the form underneath: the way a
 * takeover says "carry on to sign in".
 */
export type Takeover = (api: { close: () => void }) => ReactNode;

type AuthChatPanelProps = { overlay: PanelOverlay; takeover?: Takeover; takeoverLabel?: string };

/**
 * The right half of the auth screens: the announcements chat, which opens up into
 * the full screen signup chat.
 *
 * The slot is what holds the layout. It keeps its size while the panel inside it
 * goes position: fixed to cover the viewport, so opening and closing never move
 * the form column. See usePanelOverlay.
 *
 * Both conversations live here, and only one is running: the news chat while the
 * panel is docked, the signup chat the rest of the time. Each starts from nothing
 * when it becomes the active one.
 */
export const AuthChatPanel = ({ overlay, takeover, takeoverLabel = 'Email verification' }: AuthChatPanelProps) => {
  const { announcements, isReady } = useSigninAnnouncements();
  const isDocked = overlay.phase === 'docked';

  const news = useNewsChat({ announcements, isReady, isActive: isDocked });
  const signup = useSignupChat();

  useEffect(() => {
    // A takeover owns the full screen, so the signup chat has nothing to start.
    if (takeover) return;
    if (overlay.phase === 'open') {
      trackUmami('signup-opened');
      signup.start();
    }
    // Every way out (the back button, Escape, the confirmation's link) passes through
    // `closing`, so this is where abandonment is counted, with the stage it happened at.
    if (overlay.phase === 'closing' && signup.stage !== 'sending') {
      trackUmami('signup-closed', { stage: signup.stage });
    }
    // start() is rebuilt each render; the phase is the only thing that should
    // restart the conversation.
  }, [overlay.phase]);

  // While the account is being created, and while the chat leaves for the
  // confirmation, closing would hide the outcome: the visitor would be back on the
  // sign-in form with an account made and nothing said, or a failure played into a
  // thread nobody can see.
  const isCreating = !takeover && (signup.stage === 'sending' || signup.leaving);
  useEffect(() => {
    overlay.setLocked(isCreating);
  }, [isCreating]);

  return (
    // Full height beside the form, short when stacked under it: at the screen's height
    // the panel pushed its first message a long way down a phone, past a blank gap, and
    // people stopped scrolling before they reached any news. 910px is where the two
    // columns stop fitting side by side (420 + 460 basis, the gap and the gutters).
    <div
      ref={overlay.slotRef}
      className="h-[560px] min-w-0 flex-[1.05_1_460px] min-[910px]:h-[max(660px,calc(100vh-20px))]"
    >
      <aside
        ref={overlay.panelRef}
        aria-label={isDocked ? 'Product updates' : takeover ? takeoverLabel : 'Create an account'}
        // A dialog while it covers the screen: it is modal (the page behind is inert),
        // and focus is moved into it and back out again by usePanelOverlay.
        role={isDocked ? undefined : 'dialog'}
        aria-modal={isDocked ? undefined : true}
        tabIndex={isDocked ? undefined : -1}
        style={overlay.asideStyle}
        className={cn(
          'flex h-full w-full flex-col gap-5 overflow-hidden rounded-[16px] border border-[var(--accentLine)] px-8 pt-7 pb-6 outline-none',
          'bg-[color-mix(in_oklch,hsl(var(--accent))_7%,hsl(var(--panel)))]',
          !isDocked && 'fixed z-50',
        )}
      >
        <div
          className={cn(
            'flex min-h-0 flex-1 flex-col gap-5 transition-opacity duration-200 motion-reduce:transition-none',
            overlay.contentVisible ? 'opacity-100' : 'opacity-0',
          )}
        >
          {isDocked ? (
            <NewsPanel news={news} />
          ) : takeover ? (
            takeover({ close: overlay.close })
          ) : (
            <SignupChat signup={signup} onBack={overlay.close} />
          )}
        </div>
      </aside>
    </div>
  );
};
